import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { useAuth } from "app/providers/AuthProvider";
import {
    ConfirmActionDialog,
    FlussPageHeader,
    FlussStatusChip,
    InstanceTimeline,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { FlussRuntimeViewer } from "modules/fluss/components/FlussRuntimeViewer";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { currentNodeLabel } from "modules/fluss/utils/currentNodeLabel";
import { canOperateFluss } from "modules/fluss/utils/permissions";
import UserSelect from "modules/fluss/components/UserSelect";
import { pickerOutputLabel } from "modules/fluss/components/data-picker/outputLabel";

type InstanceAction = "retry" | "cancel" | "suspend" | "resume";

const FlussInstanceDetailPage = () => {
    const { t, i18n } = useTranslation();
    const { instanceId = "" } = useParams();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const canOperate = canOperateFluss(user);
    const [action, setAction] = useState<InstanceAction | null>(null);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState("");
    const [assigneeUserId, setAssigneeUserId] = useState("");
    const [selectedIncidentId, setSelectedIncidentId] = useState("");
    const instance = useQuery({
        queryKey: ["fluss", "monitor", "instance", instanceId],
        queryFn: () => flussApi.getInstance(instanceId),
        enabled: Boolean(instanceId) && canOperate,
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["fluss", "monitor"] });
        queryClient.invalidateQueries({ queryKey: ["fluss", "tasks"] });
    };
    const operate = useMutation({
        mutationFn: async (selected: InstanceAction) => {
            if (selected === "retry") return flussApi.retryIncident(instanceId, selectedIncidentId);
            if (selected === "cancel") return flussApi.cancelInstance(instanceId);
            if (selected === "suspend") return flussApi.suspendInstance(instanceId);
            return flussApi.resumeInstance(instanceId);
        },
        onSuccess: () => {
            setAction(null);
            setSelectedIncidentId("");
            invalidate();
        },
    });
    const reassign = useMutation({
        mutationFn: () => flussApi.reassignTask(selectedTask, assigneeUserId),
        onSuccess: () => {
            setReassignOpen(false);
            invalidate();
        },
    });

    useFlussCable({
        instanceId: canOperate ? instanceId : undefined,
        onReceived: () => queryClient.invalidateQueries({ queryKey: ["fluss", "monitor", "instance", instanceId] }),
    });

    const timeline = useMemo(() => (instance.data?.history || []).map((event) => ({
        id: event.id,
        label: event.event_type === "flow.taken" && event.edge_label
            ? t("fluss.monitor.history.flow_taken", { node: event.node_label || event.element_id || "—", path: event.edge_label, defaultValue: "{{node}} → {{path}}" })
            : event.event_type === "timer.fired"
            ? t("fluss.monitor.history.timer_fired", { node: event.node_label || "—", defaultValue: "Timer fired: {{node}}" })
            : event.event_type.startsWith("task.completed") && event.actor_name
            ? t("fluss.monitor.history.task_completed", { node: event.node_label || "—", user: event.actor_name, defaultValue: "{{node}} completed by {{user}}" })
            : event.event_type === "link.jumped"
            ? t("fluss.event.link_jumped", {
                name: String(event.payload.name || "—"),
                from: String(event.payload.throwElementId || "—"),
                to: String(event.payload.catchElementId || "—"),
                defaultValue: "Link {{name}}: {{from}} → {{to}}",
            })
            : event.event_type === "instance.cancelled" && event.payload.cancel_reason === "terminated"
            ? t("fluss.event.process_terminated", { defaultValue: "Processo encerrado" })
            : t(`fluss.event.${event.event_type.replaceAll(".", "_")}`, {
            defaultValue: t("fluss.event.activity", {
                stage: event.node_label || instance.data?.current_node_label || "—",
                defaultValue: "Activity at {{stage}}",
            }),
        }),
        detail: event.payload.error ? String(event.payload.error) : undefined,
        timestamp: event.display_time || new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "medium" }).format(new Date(event.occurred_at)),
        tone: event.event_type.includes("incident")
            ? "error" as const
            : event.event_type.includes("completed")
                ? "success" as const
                : event.event_type.includes("suspended")
                    ? "warning" as const
                    : "neutral" as const,
    })), [i18n.language, instance.data, t]);

    const formatDate = (value?: string | null) =>
        value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "—";

    if (!canOperate) return <Navigate to="/home/fluss/requests" replace />;
    if (!instance.data) return <Box className="fluss-page" />;

    const mutationError = operate.error || reassign.error;
    const error = mutationError ? flussApiError(mutationError) : null;
    const canSuspend = !["completed", "cancelled", "suspended"].includes(instance.data.status);
    const actionLabel = action ? t(`fluss.action.${action}`, { defaultValue: action }) : "";

    return (
        <Box className="fluss-page fluss-page--dense">
            <Stack gap={2}>
                <FlussPageHeader
                    title={flussDefinitionLabel(t, instance.data.definition_name)}
                    subtitle={t("fluss.monitor.instance_subtitle", { id: instance.data.id, defaultValue: "Instance {{id}}" })}
                    status={<FlussStatusChip status={instance.data.end_reason === "terminated" ? "terminated" : instance.data.status} />}
                    actions={
                        <>
                            {instance.data.open_tasks.length > 0 && <Button onClick={() => {
                                setSelectedTask(instance.data?.open_tasks[0]?.id || "");
                                setReassignOpen(true);
                            }}>{t("fluss.action.reassign", { defaultValue: "Reassign" })}</Button>}
                            {canSuspend && <Button onClick={() => setAction("suspend")}>{t("fluss.action.suspend", { defaultValue: "Suspend" })}</Button>}
                            {instance.data.status === "suspended" && <Button onClick={() => setAction("resume")}>{t("fluss.action.resume", { defaultValue: "Resume" })}</Button>}
                            {!["completed", "cancelled"].includes(instance.data.status) && <Button color="error" onClick={() => setAction("cancel")}>{t("fluss.action.cancel", { defaultValue: "Cancel instance" })}</Button>}
                        </>
                    }
                />
                {error && <Alert severity="error">{error.message}</Alert>}
                <FlussRuntimeViewer xml={instance.data.xml} state={instance.data.viewer} />
                <Box className="fluss-monitor-detail-grid">
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">{t("fluss.monitor.context_title", { defaultValue: "Business context" })}</Typography>
                        <Typography color="text.secondary">
                            {t("fluss.monitor.current_stage", { stage: currentNodeLabel(instance.data), defaultValue: "Current stage: {{stage}}" })}
                        </Typography>
                        {instance.data.anchor ? (
                            <Link
                                component={RouterLink}
                                to={`/home/workspace/${instance.data.anchor.table}/record/${instance.data.anchor.record_id}`}
                            >
                                {t("fluss.monitor.open_anchor", { defaultValue: "Open linked record" })}
                            </Link>
                        ) : (
                            <Typography color="text.secondary">{t("fluss.monitor.no_anchor", { defaultValue: "No linked business record" })}</Typography>
                        )}
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>{t("fluss.monitor.variables_title", { defaultValue: "Process variables" })}</Typography>
                        <Stack gap={1} sx={{ mt: 1 }}>
                            {(instance.data.variable_groups || []).map((group, index) => (
                                <Box key={group.node_id || `process-${index}`}>
                                    {group.node_id && <Typography variant="subtitle2">{group.node_label}</Typography>}
                                    {group.outputs.map((output) => {
                                        const label = pickerOutputLabel(t, { key: output.key, label: output.label, labelKey: output.label_key });
                                        const ref = output.value && typeof output.value === "object" && "$ref" in output.value
                                            ? (output.value as { $ref?: { table?: string; id?: string } }).$ref : undefined;
                                        const choice = typeof output.value === "string" ? output.options?.find((option) => option.value === output.value) : undefined;
                                        return <Stack key={output.key} direction="row" gap={1}>
                                            <Typography component="span" color="text.secondary">{label}:</Typography>
                                            {ref?.table && ref.id ? <Link component={RouterLink} to={`/home/workspace/${encodeURIComponent(ref.table)}/record/${encodeURIComponent(ref.id)}`}>{output.display || ref.id}</Link>
                                                : <Typography component="span">{choice
                                                    ? t(`fluss.outcome.${choice.value}`, { defaultValue: choice.label })
                                                    : typeof output.value === "object" ? JSON.stringify(output.value) : String(output.value ?? "—")}</Typography>}
                                        </Stack>;
                                    })}
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">{t("fluss.monitor.history_title", { defaultValue: "Audit history" })}</Typography>
                        <InstanceTimeline events={timeline} />
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6">{t("fluss.incidents.title", { defaultValue: "Incidents" })}</Typography>
                        {instance.data.incidents.length ? instance.data.incidents.map((incident) => (
                            <Alert
                                key={incident.id}
                                severity="error"
                                sx={{ mt: 1 }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => {
                                            setSelectedIncidentId(incident.id);
                                            setAction("retry");
                                        }}
                                    >
                                        {t("fluss.action.retry", { defaultValue: "Retry" })}
                                    </Button>
                                }
                            >
                                {incident.node_label || incident.element_id}: {incident.error || incident.reason}
                            </Alert>
                        )) : (
                            <Typography color="text.secondary">{t("fluss.incidents.none_for_instance", { defaultValue: "No incidents for this instance." })}</Typography>
                        )}
                    </Paper>
                    {(instance.data.parent_instance || instance.data.child_instances.length > 0) && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6">{t("fluss.monitor.related_title", { defaultValue: "Related processes" })}</Typography>
                            {instance.data.parent_instance && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t("fluss.monitor.related.parent", { defaultValue: "Called by" })}
                                    </Typography>
                                    <Link component={RouterLink} to={`/home/fluss/monitor/${instance.data.parent_instance.id}`}>
                                        {flussDefinitionLabel(t, instance.data.parent_instance.definition_name)}
                                    </Link>
                                    {" "}
                                    <FlussStatusChip status={instance.data.parent_instance.status} />
                                </Box>
                            )}
                            {instance.data.child_instances.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {t("fluss.monitor.related.children", { defaultValue: "Calls" })}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {instance.data.child_instances.map((child) => (
                                            <Stack key={child.id} direction="row" spacing={1} alignItems="center">
                                                <Link component={RouterLink} to={`/home/fluss/monitor/${child.id}`}>
                                                    {flussDefinitionLabel(t, child.definition_name)}
                                                </Link>
                                                <FlussStatusChip status={child.status} />
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Paper>
                    )}
                    <Paper variant="outlined" sx={{ p: 2, gridColumn: "1 / -1" }}>
                        <Typography variant="h6">{t("fluss.monitor.tokens_title", { defaultValue: "Active tokens" })}</Typography>
                        {instance.data.active_tokens.length ? (
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("fluss.monitor.tokens.node", { defaultValue: "Node" })}</TableCell>
                                            <TableCell>{t("fluss.monitor.tokens.status", { defaultValue: "Status" })}</TableCell>
                                            <TableCell>{t("fluss.monitor.tokens.parent", { defaultValue: "Parent token" })}</TableCell>
                                            <TableCell>{t("fluss.monitor.tokens.updated", { defaultValue: "Updated" })}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {instance.data.active_tokens.map((token) => (
                                            <TableRow key={token.id}>
                                                <TableCell>{token.node_label || token.element_id || "—"}</TableCell>
                                                <TableCell><FlussStatusChip status={token.status} /></TableCell>
                                                <TableCell>{token.parent_token_id || "—"}</TableCell>
                                                <TableCell>{formatDate(token.updated_at)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ) : (
                            <Typography color="text.secondary">{t("fluss.monitor.tokens.empty", { defaultValue: "No active tokens for this instance." })}</Typography>
                        )}
                    </Paper>
                </Box>
            </Stack>
            <ConfirmActionDialog
                open={Boolean(action)}
                title={actionLabel}
                description={t("fluss.action.confirm_description", { action: actionLabel, defaultValue: "Confirm {{action}}? The operation will be recorded in the audit history." })}
                confirmLabel={t("fluss.common.confirm", { defaultValue: "Confirm" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                busy={operate.isPending}
                onClose={() => setAction(null)}
                onConfirm={() => action && operate.mutate(action)}
            />
            <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t("fluss.reassign.title", { defaultValue: "Reassign task" })}</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>{t("fluss.reassign.task_label", { defaultValue: "Task" })}</InputLabel>
                            <Select
                                label={t("fluss.reassign.task_label", { defaultValue: "Task" })}
                                value={selectedTask}
                                onChange={(event) => setSelectedTask(event.target.value)}
                            >
                                {instance.data.open_tasks.map((task) => (
                                    <MenuItem key={task.id} value={task.id}>{task.title}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <UserSelect
                            label={t("fluss.reassign.user_name_label", { defaultValue: "New assignee" })}
                            value={assigneeUserId}
                            onChange={setAssigneeUserId}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReassignOpen(false)}>{t("fluss.common.cancel", { defaultValue: "Cancel" })}</Button>
                    <Button
                        variant="contained"
                        disabled={!selectedTask || !assigneeUserId || reassign.isPending}
                        onClick={() => reassign.mutate()}
                    >
                        {t("fluss.action.reassign", { defaultValue: "Reassign" })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FlussInstanceDetailPage;
